import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { LegacyUserService } from '@rma/dashboard/authentication/public/user';
import {
  CurrentSubscriptionService,
  GetSubscriptionTier,
  SubscriptionProductService,
} from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { SubscriptionTiers } from '@rma/dashboard/generic/domain/to-be-sorted';
import { NavigationHeaderComponent } from '@rma/dashboard/layout/public/navigation-header';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { differenceInDays } from 'date-fns';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { CancellationReasonService } from './../cancellation-reason.service';

@Component({
  selector: 'rma-unsubscribe-feature-warning',
  imports: [AsyncPipe, MatButtonModule, MatCardModule, NavigationHeaderComponent, RmaIconComponent, RouterModule, TranslocoModule],
  templateUrl: './unsubscribe-feature-warning.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('subscriptions', 'unsubscribeFeatures')],
})
export class UnsubscribeFeatureWarningComponent {
  private readonly cancellationReasonService = inject(CancellationReasonService);
  private readonly subscriptionService = inject(CurrentSubscriptionService);
  private readonly legacyUserService = inject(LegacyUserService);
  private readonly router = inject(Router);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly subscriptionProductService = inject(SubscriptionProductService);

  public featureKeys$: Observable<string[] | null>;

  constructor() {
    this.featureKeys$ = combineLatest([
      this.currentSubscriptionService.currentSubscription$,
      this.subscriptionProductService.currentSubscriptionProducts$,
    ]).pipe(
      map(([currentSub, plans]) => {
        const currentPlan = plans.find(
          (p) => GetSubscriptionTier(p.Tier) === currentSub?.tier && p.ProductUserTypeGrouping === currentSub.productUserTypeGrouping,
        );
        const downgradedPlan = plans.find(
          (p) => GetSubscriptionTier(p.Tier) === SubscriptionTiers.Free && p.ProductUserTypeGrouping === currentSub.productUserTypeGrouping,
        );

        if (!currentPlan) {
          return null;
        }

        const permittedFeatures = currentPlan.Features?.filter((f) => f !== 'personalised-onboarding-extra-cost') ?? [];
        if (!downgradedPlan) {
          return permittedFeatures;
        }

        return permittedFeatures.filter((f) => !downgradedPlan.Features?.includes(f));
      }),
    );
  }

  trialDaysDiff(endDate: Date): number {
    const currentDateMinusOneDay = new Date().setDate(new Date().getDate() - 1);
    return differenceInDays(endDate, currentDateMinusOneDay);
  }

  onDowngrade(): void {
    combineLatest([
      this.cancellationReasonService.cancellationReason$,
      this.cancellationReasonService.cancellationFeedback$,
      this.cancellationReasonService.cancellationCompetitor$,
      this.cancellationReasonService.otherTool$,
    ])
      .pipe(
        take(1),
        switchMap(([reason, feedback, competitor, otherTool]) =>
          this.subscriptionService.cancelSubscription([reason], feedback, competitor, otherTool),
        ),
      )
      .subscribe({
        complete: () => {
          this.legacyUserService.refreshUserInfo();
          this.router.navigateByUrl('/billing/unsubscribe-complete');
        },
        error: (err) => {
          console.error('An error occurred during the cancellation process:', err);
        },
      });
  }
}
